import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Calendar, ArrowUpRight, Newspaper } from 'lucide-react';
import axios from 'axios';
import { news as mockNews } from '../data/mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/admin/news/public/list?limit=6&featured_only=true`);
      if (response.data && response.data.length > 0) {
        setNewsItems(response.data.slice(0, 6));
      } else {
        setNewsItems(mockNews);
      }
    } catch (error) {
      console.error('Failed to fetch news:', error);
      setNewsItems(mockNews);
    } finally {
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  // Get the appropriate fields from either backend or mock data
  const getNewsTitle = (item) => item.title;
  const getNewsDate = (item) => item.published_date ? formatDate(item.published_date) : item.date;
  const getNewsSource = (item) => item.source_name || item.source;
  const getNewsId = (item) => item.article_id || item.id;

  return (
    <section id="news" className="py-24 bg-white relative" aria-label="Latest news">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Section Header - centered */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <span className="text-[#C8473A] font-medium uppercase tracking-wider text-sm">Latest Updates</span>
          <h2 className="text-4xl md:text-5xl font-bold text-[#211F1F] mt-4">
            In the <span className="text-[#C8473A]">News</span>
          </h2>
          <Link
            to="/news"
            className="mt-4 inline-flex items-center gap-2 text-[#C8473A] font-medium hover:gap-3 transition-all mx-auto justify-center"
            data-testid="view-all-news-link"
            aria-label="View all news"
          >
            View All <ArrowUpRight className="w-5 h-5" aria-hidden="true" />
          </Link>
        </motion.div>

        {/* News Grid */}
        {loading ? (
          <div className="grid md:grid-cols-3 gap-8">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-100 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="h-6 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-6 bg-gray-200 rounded w-3/4"></div>
              </div>
            ))}
          </div>
        ) : newsItems.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <Newspaper className="w-12 h-12 mx-auto text-gray-300 mb-4" aria-hidden="true" />
            <p className="text-gray-500">News updates coming soon</p>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8" data-testid="news-grid">
            {newsItems.map((item, index) => (
              <motion.div
                key={getNewsId(item)}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
                data-testid={`news-item-${getNewsId(item)}`}
              >
                <Link
                  to={`/news?open=${encodeURIComponent(getNewsId(item))}`}
                  className="block h-full"
                  aria-label={`Read news: ${getNewsTitle(item)}`}
                >
                  <article className="h-full bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-100 hover:border-[#C8473A]/30 hover:shadow-xl transition-all duration-300">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                      <Calendar className="w-4 h-4" aria-hidden="true" />
                      <span>{getNewsDate(item)}</span>
                      <span className="text-gray-300">|</span>
                      <span>{getNewsSource(item)}</span>
                    </div>
                    <h3 className="heading-subsection group-hover:text-[#C8473A] transition-colors leading-snug">
                      {getNewsTitle(item)}
                    </h3>
                    <div className="mt-4 flex items-center gap-2 text-[#C8473A] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      Read More <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
                    </div>
                  </article>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default NewsSection;
